import { supabase } from "@/integrations/supabase/client";

/**
 * Resolve a viewable URL for a file in the academic-files bucket.
 *
 * Tolerates:
 *  - bare object paths (e.g. "students/<id>/passport_photo.png")
 *  - old public URLs containing "/storage/v1/object/public/academic-files/"
 *  - already-signed URLs containing "/storage/v1/object/sign/academic-files/"
 *  - any query string / cache-buster appended to the above
 *
 * Strategy:
 *  1) createSignedUrl (1-hour signed URL).
 *  2) Fallback: download the object and return an object URL — handles transient
 *     storage-schema errors (e.g. DatabaseInvalidObjectDefinition).
 */
export async function getSignedFileUrl(fileUrlOrPath: string): Promise<string | null> {
  if (!fileUrlOrPath) return null;

  // Strip any query string before parsing
  let raw = fileUrlOrPath.split("?")[0];

  // Extract path from old public/sign URLs if needed
  const publicMarker = "/storage/v1/object/public/academic-files/";
  const signMarker = "/storage/v1/object/sign/academic-files/";
  const authMarker = "/storage/v1/object/authenticated/academic-files/";

  let path = raw;
  for (const marker of [publicMarker, signMarker, authMarker]) {
    if (raw.includes(marker)) {
      path = raw.split(marker)[1];
      break;
    }
  }

  // Decode any %-encoding in the path segment
  try {
    path = decodeURIComponent(path);
  } catch {
    /* leave as-is if not valid URI */
  }

  // 1) Try a signed URL first
  const { data, error } = await supabase.storage
    .from("academic-files")
    .createSignedUrl(path, 3600);

  if (!error && data?.signedUrl) {
    return data.signedUrl;
  }

  // 1b) Fallback through a verified backend function. Some private storage
  // signing endpoints can fail client-side even when the authenticated user is
  // allowed to view the object, which made captured biometrics show as missing.
  try {
    const { data: functionData, error: functionError } = await supabase.functions.invoke("get-academic-file-url", {
      body: { path },
    });

    if (!functionError && functionData?.signedUrl) {
      return functionData.signedUrl;
    }
  } catch (functionErr) {
    console.error("[getSignedFileUrl] backend signed URL fallback failed", { path, functionErr });
  }

  // 2) Fallback: download the object and return a blob URL
  try {
    const { data: blob, error: dlErr } = await supabase.storage
      .from("academic-files")
      .download(path);

    if (dlErr || !blob) {
      console.error("[getSignedFileUrl] sign + download failed", { path, signErr: error, dlErr });
      return null;
    }

    return URL.createObjectURL(blob);
  } catch (e) {
    console.error("[getSignedFileUrl] unexpected error", { path, e });
    return null;
  }
}
